import React, { useCallback, useEffect, useState } from 'react';

import Checkbox from '../CheckBox';
import Fieldset from '../shared/Fieldset';

import s from './Checkboxes.module.scss';

export type CheckboxOption = {
  key: string;
  label: string;
};

export type CheckboxesProps = {
  options: CheckboxOption[];
  label: string;
  description?: string;
  disabled?: boolean;
  hint?: string;
  value: string[] | null;
  optional?: boolean;
  onChange: (value: string[]) => void;
  onFocus?: () => void;
  compact?: boolean;
  error?: string;
};

function isValueChecked(checkedValues: unknown, key: string): boolean {
  if (Array.isArray(checkedValues)) {
    return checkedValues.includes(key);
  }
  return [checkedValues].includes(key);
}

const Checkboxes: React.FC<CheckboxesProps> = ({
  options,
  optional,
  label,
  description,
  hint,
  value,
  onChange,
  onFocus,
  disabled,
  compact,
  error,
}) => {
  const [checkedValues, setCheckedValues] = useState<string[]>(value ?? []);

  useEffect(() => {
    setCheckedValues(value ?? []);
  }, [value]);

  const handleChange = useCallback(
    (v: string, newIsChecked: boolean): void => {
      setCheckedValues((prev) => {
        const newValue = newIsChecked ? [...prev, v] : prev.filter((val) => val !== v);
        onChange(newValue);
        return newValue;
      });
    },
    [onChange],
  );

  return (
    <Fieldset error={error} label={label} description={description} hint={hint} required={!optional}>
      {options.map((option) => (
        <div key={option.key} className={s.item}>
          <Checkbox
            className={s.checkbox}
            disabled={disabled}
            id={option.key}
            isChecked={isValueChecked(checkedValues, option.key)}
            label={option.label}
            onChange={(newIsChecked) => handleChange(option.key, newIsChecked)}
            onFocus={onFocus}
            size={compact ? 'sm' : 'md'}
          />
        </div>
      ))}
    </Fieldset>
  );
};

export default Checkboxes;
